import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('wms_token');
      localStorage.removeItem('wms_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const checkIP = () => api.post('/auth/check-ip');

// Users
export const getUsers = () => api.get('/users');
export const inviteUser = (data) => api.post('/users/invite', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Locations
export const getLocations = (params) => api.get('/locations', { params });
export const getLocation = (slot) => api.get(`/locations/${slot}`);
export const getRackLocations = (rackCode) => api.get(`/locations/rack/${rackCode}`);
export const toggleLocation = (slot) => api.put(`/locations/${slot}/toggle`);

// Warehouse Map
export const getWarehouseMap = () => api.get('/warehouse-map');

// Pallets
export const newPackingSession = () => api.post('/pallets/new-session');
export const getPallets = (params) => api.get('/pallets', { params });
export const getPallet = (id) => api.get(`/pallets/${id}`);
export const updatePallet = (id, data) => api.put(`/pallets/${id}`, data);
export const addItem = (palletId, data) => api.post(`/pallets/${palletId}/items`, data);
export const updateItem = (id, data) => api.put(`/items/${id}`, data);
export const addBox = (palletId, data) => api.post(`/pallets/${palletId}/boxes`, data);
export const generateBoxes = (palletId) => api.post(`/pallets/${palletId}/generate-boxes`);
export const finalizePallet = (palletId) => api.post(`/pallets/${palletId}/finalize`);
export const placePallet = (palletId, data) => api.post(`/pallets/${palletId}/place`, data);
export const movePallet = (palletId, data) => api.post(`/pallets/${palletId}/move`, data);
export const issuePallet = (palletId, data) => api.post(`/pallets/${palletId}/issue`, data);
export const qcHoldPallet = (palletId, data) => api.post(`/pallets/${palletId}/qc-hold`, data);
export const qcClearPallet = (palletId, data) => api.post(`/pallets/${palletId}/qc-clear`, data);
export const voidPallet = (palletId) => api.post(`/pallets/${palletId}/void`);

// Customers
export const getCustomers = () => api.get('/customers');
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);

// GRN
export const getGRNs = () => api.get('/grn');
export const createGRN = (data) => api.post('/grn', data);
export const confirmGRN = (id) => api.post(`/grn/${id}/confirm`);

// Reports
export const getStockAging = () => api.get('/reports/stock-aging');
export const getOccupancy = () => api.get('/reports/occupancy');
export const getMovementLog = (params) => api.get('/reports/movement-log', { params });
export const getAuditLog = (params) => api.get('/reports/audit-log', { params });

// Challans
export const getChallans = () => api.get('/challans');
export const getChallan = (id) => api.get(`/challans/${id}`);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSetting = (key, value) => api.put('/settings', { key, value });

// IP Allowlist
export const getIPAllowlist = () => api.get('/ip-allowlist');
export const addIP = (data) => api.post('/ip-allowlist', data);
export const removeIP = (id) => api.delete(`/ip-allowlist/${id}`);

// QR
export const qrLookup = (data) => api.post('/qr/lookup', data);
export const detectCategory = (description) => api.post('/detect-category', { description });

// FIFO
export const getFIFOSuggestion = (itemCode) => api.get(`/fifo-suggestion/${itemCode}`);

// Cycle Count
export const getCycleCounts = () => api.get('/cycle-counts');
export const createCycleCount = (data) => api.post('/cycle-counts', data);

// Export
export const exportInventory = () => api.get('/export/inventory');
export const triggerBackup = () => api.post('/export/backup-now');

// Labels
export const getSlotLabel = (slot) => api.get(`/labels/slot/${slot}`);
export const getPalletLabel = (palletId) => api.get(`/labels/pallet/${palletId}`);
export const reprintPalletLabel = (palletId) => api.post(`/labels/pallet/${palletId}/reprint`);
export const getBoxLabel = (boxId) => api.get(`/labels/box/${boxId}`);
export const getAllBoxLabels = (palletId) => api.get(`/labels/boxes/pallet/${palletId}`);
export const getCompanionBoard = (rackCode) => api.get(`/labels/companion-board/${rackCode}`);
export const getChallanPDF = (challanId) => api.get(`/labels/challan/${challanId}`);

// Split / Merge
export const splitPallet = (palletId, data) => api.post(`/pallets/${palletId}/split`, data);
export const mergePallet = (palletId, data) => api.post(`/pallets/${palletId}/merge`, data);

// Sales Returns
export const createSalesReturn = (data) => api.post('/returns', data);

// Cycle Count (enhanced)
export const startCycleCount = (id) => api.post(`/cycle-counts/${id}/start`);
export const verifyCycleCountSlot = (id, data) => api.post(`/cycle-counts/${id}/verify`, data);
export const resolveCycleCountDiscrepancy = (id, data) => api.post(`/cycle-counts/${id}/resolve`, data);
export const completeCycleCount = (id) => api.post(`/cycle-counts/${id}/complete`);
export const getCycleCountDetail = (id) => api.get(`/cycle-counts/${id}`);

// Putaway
export const getPutawaySuggestion = (itemCode) => api.get(`/putaway-suggestion/${itemCode}`);

// Pick List
export const generatePickList = (palletIds) => api.post('/pick-list', { pallet_ids: palletIds });

// Warehouse
export const getWarehouse = () => api.get('/warehouse');

// Abandoned Sessions
export const getAbandonedSessions = () => api.get('/abandoned-sessions');
export const continueSession = (palletId) => api.post(`/abandoned-sessions/${palletId}/continue`);
export const cancelSession = (palletId) => api.post(`/abandoned-sessions/${palletId}/cancel`);
export const extendSession = (palletId) => api.post(`/abandoned-sessions/${palletId}/extend`);

// Draft (in-progress) pallets
export const getPalletDrafts = (mine = false) => api.get('/pallet-drafts', { params: mine ? { mine: true } : {} });

// Sync Conflicts
export const getConflicts = () => api.get('/conflicts');
export const resolveConflict = (slotAddress, data) => api.post(`/conflicts/${encodeURIComponent(slotAddress)}/resolve`, data);

// Slot Labels
export const getSlotLabelsPDF = (rackCode) => api.get(`/labels/slot-labels/${rackCode}`);
export const getSlotLabelsRacks = () => api.get('/labels/slot-labels/racks');
export const generateAllSlotLabels = () => api.post('/labels/generate-all-slot-labels');
export const getSlotLabelsZipStatus = () => api.get('/labels/slot-labels-zip/status');
export const getLabelAffixingManual = () => api.get('/labels/affixing-manual');
export const getQRStatus = () => api.get('/labels/qr-status');
export const preGenerateQRCodes = () => api.post('/labels/pre-generate-qr');

// Admin: Data Management (POST methods bypass proxies/WAFs that block DELETE/PUT)
export const adminListAuditLog = (params) => api.get('/admin/audit-log', { params });
export const adminListRecycledRefs = () => api.get('/admin/recycled-refs');
export const adminDropRecycledRef = (data) => api.post('/admin/recycled-refs/drop', data);
export const adminHardDeletePallet = (id) => api.post(`/admin/pallets/${id}/hard-delete`);
export const adminForceEditPallet = (id, data) => api.post(`/admin/pallets/${id}/force-edit`, data);
export const adminHardDeleteItem = (id) => api.post(`/admin/items/${id}/hard-delete`);
export const adminHardDeleteGRN = (id) => api.post(`/admin/grn/${id}/hard-delete`);
export const adminHardDeleteChallan = (id) => api.post(`/admin/challans/${id}/hard-delete`);

// Tool Store (Phase 2A)
export const tsListMachines = () => api.get('/toolstore/machines');
export const tsCreateMachine = (d) => api.post('/toolstore/machines', d);
export const tsDeleteMachine = (id) => api.delete(`/toolstore/machines/${id}`);
export const tsListVendors = () => api.get('/toolstore/vendors');
export const tsCreateVendor = (d) => api.post('/toolstore/vendors', d);
export const tsPreviewFunctionalId = (d) => api.post('/toolstore/tools/functional-id/preview', d);
export const tsListTools = (params) => api.get('/toolstore/tools', { params });
export const tsGetTool = (id) => api.get(`/toolstore/tools/${id}`);
export const tsCreateTool = (d) => api.post('/toolstore/tools', d);
export const tsUpdateTool = (id, d) => api.put(`/toolstore/tools/${id}`, d);
export const tsIssueTool = (id, d) => api.post(`/toolstore/tools/${id}/issue`, d);
export const tsReturnTool = (id, d) => api.post(`/toolstore/tools/${id}/return`, d);
export const tsStats = () => api.get('/toolstore/stats');

export default api;
